import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { changePassword, resetUserPassword } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";

const MIN = 8;

/**
 * One dialog for both jobs. With `target` it is the superadmin setting someone
 * else's password; without it, anyone changing their own -- which demands the
 * current password, because knowing the old one is what proves it is you.
 */
export default function PasswordDialog({
  target,
  onClose,
}: {
  target?: { id: number; email: string };
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: () => (target ? resetUserPassword(target.id, next) : changePassword(current, next)),
    onSuccess: () => setDone(true),
  });

  const tooShort = next.length > 0 && next.length < MIN;
  const mismatch = again.length > 0 && next !== again;
  const ready = next.length >= MIN && next === again && (target ? true : current.length > 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {target ? t("pw.resetTitle", { email: target.email }) : t("pw.changeTitle")}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <p className="text-slate-300">
            {target ? t("pw.resetDone", { email: target.email }) : t("pw.changeDone")}
          </p>
        ) : (
          <div className="space-y-3">
            {!target && (
              <label className="block space-y-1">
                <span className="text-xs text-slate-400">{t("pw.current")}</span>
                <Input type="password" autoComplete="current-password" autoFocus
                       value={current} onChange={(e) => setCurrent(e.target.value)} />
              </label>
            )}
            <label className="block space-y-1">
              <span className="text-xs text-slate-400">{t("pw.new")}</span>
              <Input type="password" autoComplete="new-password" autoFocus={!!target}
                     value={next} onChange={(e) => setNext(e.target.value)} />
            </label>
            <label className="block space-y-1">
              <span className="text-xs text-slate-400">{t("pw.confirm")}</span>
              <Input type="password" autoComplete="new-password"
                     value={again} onChange={(e) => setAgain(e.target.value)} />
            </label>
            {tooShort && <p className="text-sm text-signal-warn">{t("pw.tooShort", { n: MIN })}</p>}
            {mismatch && <p className="text-sm text-signal-warn">{t("pw.mismatch")}</p>}
            <p className="text-xs text-slate-500">{t(target ? "pw.resetNote" : "pw.changeNote")}</p>
            {save.error && <p className="text-sm text-signal-crit">{String(save.error)}</p>}
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button onClick={onClose}>{t("common.close")}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
              <Button disabled={!ready || save.isPending} onClick={() => save.mutate()}>
                {t("common.save")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
